import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination, Table as TableAntd } from 'antd';
import { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, GetRowKey, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import { SizeType } from 'antd/lib/config-provider/SizeContext';

import Nodata from 'resources/images/nodata_icon.png';

import LENGTH_CONSTANTS from 'constants/length';

const { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS, DEFAULT_TOTAL } = LENGTH_CONSTANTS;

type TableProps = {
  columns?: Array<any>;
  dataSource?: readonly any[];
  current?: number;
  pageSize?: number;
  total: number;
  rowClassName?: string;
  pageSizeOptions?: string[];
  onChangeTable?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
    extra: TableCurrentDataSource<any>,
  ) => void;
  onChangePagination?: (page: number, pageSize: number) => void;
  size?: SizeType;
  bordered?: boolean;
  rowKey?: string | GetRowKey<any>;
  className?: string;
  scroll?: any;
  loading?: boolean;
  showTotal?: boolean;
  showSizeChanger?: boolean;
  showPagination?: boolean;
  emptyText?: string;
};

const Table: FC<TableProps> = ({
  columns,
  dataSource,
  current,
  pageSize,
  total,
  rowClassName,
  pageSizeOptions,
  onChangeTable,
  onChangePagination,
  size,
  bordered,
  rowKey,
  className,
  scroll,
  emptyText,
  loading,
}) => {
  const { t } = useTranslation();

  const renderEmptyData = useMemo(() => {
    return (
      <div className="ant-empty-text">
        <img src={Nodata} />
        <p>{emptyText || t('common.txt_no_data')}</p>
      </div>
    );
  }, [emptyText]);

  return (
    <div className="table">
      <TableAntd
        locale={{
          emptyText: renderEmptyData,
        }}
        pagination={false}
        columns={columns}
        bordered={bordered}
        rowClassName={rowClassName}
        dataSource={dataSource}
        onChange={onChangeTable}
        size={size}
        showSorterTooltip={false}
        rowKey={rowKey}
        className={className}
        scroll={scroll || { x: 1024 }}
        loading={loading}
      />
      {total > 0 && (
        <div className="pagination">
          <Pagination
            size="small"
            total={total}
            current={current}
            onChange={onChangePagination}
            pageSizeOptions={pageSizeOptions}
            pageSize={pageSize}
            showSizeChanger
          />
        </div>
      )}
    </div>
  );
};

Table.defaultProps = {
  pageSize: DEFAULT_PAGE_SIZE,
  pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
  total: DEFAULT_TOTAL,
  current: DEFAULT_PAGE,
};

export default Table;
